import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, TextField, IconButton, Menu, MenuItem } from '@mui/material';
import { KeyRound, Lightbulb, Bell, Gift, MoreVertical, Trash2, Edit2 } from 'lucide-react'; // ArrowRight
import { useNavigate } from 'react-router-dom';
import GradientText from './common/GradientText.tsx';

interface UseCaseLinkProps {
  id?: string;
  icon?: string;
  text?: string;
  isTemplate?: boolean;
  isDraggable?: boolean;
  isPlaced?: boolean;
  onDelete?: (id: string) => void;
  onUpdate?: (id: string, text: string) => void;
}

const LinkContainer = styled(Box, {
  shouldForwardProp: (prop) => 
    prop !== 'isTemplate' && prop !== 'isDraggable' && prop !== 'isPlaced'
})<{
  isTemplate?: boolean;
  isDraggable?: boolean;
  isPlaced?: boolean;
}>(({ theme, isTemplate, isDraggable, isPlaced }) => ({
  textDecoration: 'none',
  display: 'block',
  width: '210px',
  height: '80px',
  background: '#fff',
  textAlign: 'left',
  paddingLeft: '20px',
  transform: isTemplate ? 'rotate(-30deg) skew(25deg) translate(0,0)' : 'none',
  transition: '.5s',
  boxShadow: isTemplate ? '-20px 20px 10px rgba(0,0,0,.5)' : 'none',
  position: 'relative',
  cursor: isDraggable ? 'grab' : 'pointer',

  ...(isTemplate && {
    '&:before': {
      content: '""',
      position: 'absolute',
      top: '10px',
      left: '-20px',
      height: '100%',
      width: '20px',
      background: '#b1b1b1',
      transform: 'rotate(0deg) skewY(-45deg)',
      transition: '.5s',
    },

    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: '-20px',
      left: '-10px',
      height: '20px',
      width: '100%',
      background: '#b1b1b1',
      transform: 'rotate(0deg) skewX(-45deg)',
      transition: '.5s',
    },

    '&:hover': {
      transform: 'rotate(-30deg) skew(25deg) translate(20px,-15px)',
      boxShadow: '-50px 50px 50px rgba(0,0,0,.5)',
      background: '#8a2be2',

      '&:before': {
        background: '#6a1b9a',
      },

      '&:after': {
        background: '#9c27b0',
      },

      '& .icon': {
        color: '#00CC99',
      },

      '& .text': {
        color: '#fff',
      },
    },
  }),

  ...(isPlaced && {
    background: 'rgba(26, 26, 46, 0.6)',
    backdropFilter: 'blur(5px)',
    borderRadius: theme.spacing(2),
    border: '1px solid rgba(138, 43, 226, 0.2)',
    width: '100%',
    
    '&:hover': {
      background: ({ palette }) => {
        const colors = [
          'rgba(0, 51, 102, 0.8)',   // Eterna Blue
          'rgba(0, 204, 153, 0.8)',  // Aurora Green
          'rgba(138, 43, 226, 0.8)', // Pulse Purple
          'rgba(255, 111, 0, 0.8)',  // Energy Orange
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      },
    },
  }),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  fontSize: '40px',
  color: '#00CC99',
  lineHeight: '80px',
  transition: '.5s',
  paddingRight: '14px',
  display: 'flex',
  alignItems: 'center',
}));

const Text = styled(Typography)({
  padding: 0,
  margin: 0,
  position: 'absolute',
  top: '30px',
  left: '80px',
  color: '#262626',
  letterSpacing: '4px',
  transition: '.5s',
  fontSize: '14px',
  fontWeight: 500,
});

const getIcon = (type: string) => {
  switch (type) {
    case 'key': return <KeyRound size={32} className="icon" />;
    case 'idea': return <Lightbulb size={32} className="icon" />;
    case 'alert': return <Bell size={32} className="icon" />;
    default: return <Gift size={32} className="icon" />;
  }
};

const ActionButtons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: theme.spacing(1),
  opacity: 0,
  transition: 'opacity 0.3s ease',
  '.parent:hover &': {
    opacity: 1,
  },
}));

const UseCaseLink: React.FC<UseCaseLinkProps> = ({ 
  id = 'template',
  icon = 'key',
  text: initialText = 'Click to edit',
  isTemplate = false,
  isDraggable = true,
  isPlaced = false,
  onDelete,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(initialText);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.setData('application/usecase', JSON.stringify({
      id,
      icon,
      text
    }));
  };

  const handleClick = () => {
    if (isPlaced) {
      navigate('/proposal-detail/1');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsEditing(true);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditing(true);
    handleMenuClose();
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
    handleMenuClose();
  };

  const handleTextChange = (newText: string) => {
    setText(newText);
    if (onUpdate) {
      onUpdate(id, newText);
    }
  };

  return (
    <LinkContainer
      className="parent"
      isTemplate={isTemplate}
      isDraggable={isDraggable}
      isPlaced={isPlaced}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onClick={handleClick}
    >
      <IconWrapper>
        {getIcon(icon)}
      </IconWrapper>
      
      {isEditing ? (
        <TextField
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          onBlur={() => setIsEditing(false)}
          onKeyPress={(e) => e.key === 'Enter' && setIsEditing(false)}
          autoFocus
          fullWidth
          variant="standard"
          sx={{ 
            input: { 
              color: '#262626',
              fontSize: '0.9rem',
            }
          }}
        />
      ) : (
        <>
          <Text className="text">
          <GradientText variant="h8" underline="always" gradient="secondary">{text}</GradientText>
            {isPlaced && (
              <GradientText variant="h7" underline="always" gradient="primary">Build Now</GradientText>
            )}
          </Text>

          {isPlaced && (
            <ActionButtons>
              <IconButton 
                size="medium" 
                onClick={handleMenuOpen}
                sx={{ color: 'rgba(0, 204, 153, 0.8)' }}
              >
                <MoreVertical size={16} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleEdit}>
                  <Edit2 size={16} style={{ marginRight: 8 }} />
                  Edit
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                  <Trash2 size={16} style={{ marginRight: 8 }} />
                  Delete
                </MenuItem>
              </Menu>
            </ActionButtons>
          )}
        </>
      )}
    </LinkContainer>
  );
};

export default UseCaseLink; 